import { store } from '@redux/store';
import { fromEvent } from 'rxjs';
import getAllQuestions$ from '../apis/getAllQuestions';
import ModalStep from '../enums/modalStep';
import UserStatus from '../enums/userStatus';
import {
  scheduleTour,
  setModalStep,
  setQuestionsResponses
} from '../redux/slices';
import question from './question';
import questionOption from './questionOption';
import { selectBookingStatus } from '../redux/selectors';
import BookingStatus from '../enums/bookingStatus';

export default class Questions {
  // this is to pacify the eslint rule
  htmlClass = 'nhs-c-self-guided-left-panel';

  // Properties
  listContainerSelector = '[data-self-guided-tour-questions-list]';

  optionsListContainerSelector =
    '[data-self-guided-tour-question-options-list-{id}]';

  previousStep = ModalStep.SignIn;

  questionsSubscription$ = null;

  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.previousStep = ModalStep.SignIn;
    this.rootComponent.questions = {};
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  /**
   * Handles DOM updates, including initiating fetching of the questions.
   * @param {Event} event - The DOM update event.
   */
  onDomUpdate(event) {
    if (event.detail?.step === ModalStep.Questions) {
      this.rootComponent.setPrimaryButtonDisabled(false);
      if (this.rootComponent.userStatus.name === UserStatus.Verified.name) {
        this.fetchTourRelatedQuestions();
      } else {
        this.fetchAllQuestions();
      }
      this.rootComponent.setSecondaryButtonDisabled(true);
      this.rootComponent.setPrimaryButtonDisabled(true);
      this.setupEventHandlers();
    } else {
      this.teardownEventHandlers();
    }
  }

  setupEventHandlers() {
    const questionsContainer = this.getListContainer();
    this.questionsSubscription$ = fromEvent(
      questionsContainer,
      'change'
    ).subscribe(this.onOptionRadioButtonChange.bind(this));
  }

  teardownEventHandlers() {
    if (this.questionsSubscription$) {
      this.questionsSubscription$.unsubscribe();
      this.questionsSubscription$ = null;
    }
  }

  /**
   * Handles the change event for any option.
   *
   */
  onOptionRadioButtonChange() {
    const state = store.getState();
    const bookingStatus = selectBookingStatus(state);

    // Check if the booking status is failed, if so, do not re-enable the button
    if (bookingStatus === BookingStatus.Failed) {
      return;
    }
    const questionsContainer = this.getListContainer();
    // Get selected options
    let options = questionsContainer.querySelectorAll('input');
    let selectedOptions = Array.from(options).filter(option => option.checked);

    // Hide/show child questions based on the selected options
    const childQuestions = questionsContainer.querySelectorAll(
      '[data-parent-question-option-id]'
    );
    childQuestions.forEach(childQuestion => {
      const parentOptionId = childQuestion.getAttribute(
        'data-parent-question-option-id'
      );
      const exists = selectedOptions.some(
        option => option.id === parentOptionId
      );
      childQuestion.hidden = !exists;
      // Uncheck all child options if the parent is hidden
      if (childQuestion.hidden) {
        const childOptions = childQuestion.querySelectorAll('input');
        childOptions.forEach(childOption => {
          childOption.checked = false;
        });
      }
    });

    // Hide/show child options based on the selected options
    let emptyChildOptions = false;
    const childOptions = questionsContainer.querySelectorAll(
      '[data-parent-option-id]'
    );
    childOptions.forEach(childOption => {
      const parentOptionId = childOption.getAttribute('data-parent-option-id');
      const exists = selectedOptions.some(
        option => option.id === parentOptionId
      );
      childOption.hidden = !exists;
      const input = childOption.querySelector('input');
      if (exists && input && !input.value.trim()) {
        emptyChildOptions = true;
      }
    });

    // Get shown questions
    const questions = questionsContainer.querySelectorAll(
      '[data-question-section]'
    );
    const visibleQuestions = Array.from(questions).filter(item => !item.hidden);
    // Refresh the selected options
    options = questionsContainer.querySelectorAll('input');
    selectedOptions = Array.from(options).filter(option => option.checked);

    // Enable the primary button only if all visible questions have been answered and there are no empty child options,
    // since all the questions options are radio buttons we must have the same number of selected options as questions
    const allQuestionsAnswered =
      selectedOptions.length === visibleQuestions.length;
    this.rootComponent.setPrimaryButtonDisabled(
      !allQuestionsAnswered || emptyChildOptions
    );
  }

  /**
   * Handles the primary action for the tour configuration.
   * Dispatches an action to update the modal step in the store with the next step.
   *
   */
  handlePrimaryAction() {
    const questionsContainer = this.getListContainer();
    const options = questionsContainer.querySelectorAll('input');
    const selectedOptions = Array.from(options).filter(
      option => option.checked
    );
    let selectedOptionsIds = selectedOptions.map(option => ({
      questionId: option.getAttribute('data-question-id'),
      parentText: option.getAttribute('data-parent-text'),
      questionText: option.getAttribute('data-question-text'),
      responseId: option.value
    }));
    // Child options (not radio buttons) are not included in the selected options
    const childOptions = questionsContainer.querySelectorAll(
      '[data-parent-option-id]'
    );
    childOptions.forEach(childOption => {
      if (!childOption.hidden) {
        const input = childOption.querySelector('input');
        // Exclude the parent option from the selected options (must be sent only one option per question)
        selectedOptionsIds = selectedOptionsIds.filter(
          item => item.questionId !== input.getAttribute('data-question-id')
        );
        // Add the child option as the selected option
        selectedOptionsIds.push({
          parentText: input.getAttribute('data-parent-text'),
          questionId: input.getAttribute('data-question-id'),
          questionText: input.getAttribute('data-question-text'),
          responseId: input.id,
          content: input.value
        });
      }
    });

    store.dispatch(
      setQuestionsResponses({ questionsResponses: selectedOptionsIds })
    );
    // Disable the primary button to prevent multiple clicks
    this.rootComponent.setPrimaryButtonDisabled(true);
    store.dispatch(scheduleTour());
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   *
   */
  handleSecondaryAction() {
    store.dispatch(setModalStep({ modalStep: this.previousStep }));
  }

  /**
   * Fetches the list of tour related questions.
   * The fetched data is stored in `otherTourableHomes` and updates the card list in the UI.
   * Uses the `getTourRelatedQuestions` API to retrieve the questions based on the current spec ID.
   */
  fetchTourRelatedQuestions() {
    // The tour related questions are fetched at the beginning of the flow to be checked during T&Cs step
    this.rootComponent.questions = this.rootComponent.tourQuestions;
    this.updateQuestionsList();
  }

  /**
   * Fetches the list of tour related questions.
   * The fetched data is stored in `otherTourableHomes` and updates the card list in the UI.
   * Uses the `getTourRelatedQuestions` API to retrieve the questions based on the current spec ID.
   */
  fetchAllQuestions() {
    const { specId } = this.rootComponent.getSpecDetails();
    getAllQuestions$(specId).subscribe({
      next: questions => {
        const questionsCollection = {};
        questions.forEach(item => {
          questionsCollection[item.Id] = item;
        });
        this.rootComponent.questions = questionsCollection;
        this.updateQuestionsList();
      }
    });
  }

  /**
   * Retrieves the DOM element for the list container.
   * @returns {HTMLElement} The list container element.
   */
  getListContainer() {
    return document.querySelector(this.listContainerSelector);
  }

  /**
   * Retrieves the DOM element for the list container.
   * @returns {HTMLElement} The option list container element.
   */
  getOptionsListContainer(id) {
    return document.querySelector(
      this.optionsListContainerSelector.replace('{id}', id)
    );
  }

  /**
   * Updates the question list in the UI with the fetched questions for the spec.
   */
  updateQuestionsList() {
    const listContainer = this.getListContainer();

    const { brandName } = this.rootComponent.getSpecDetails();
    const questions = Object.values(this.rootComponent.questions);

    // Considering edge case where there are no questions and user gets to the questions step
    if (!questions || questions.length === 0) {
      this.rootComponent.setPrimaryButtonDisabled(false);
      return;
    }

    listContainer.innerHTML = questions
      .map(item =>
        question({
          text: item.Text,
          id: item.Id,
          parentQuestionOptionId: item.ParentQuestionOptionId
        })
      )
      .join('');

    questions.forEach(item => {
      const optionsListContainer = this.getOptionsListContainer(item.Id);
      if (optionsListContainer) {
        optionsListContainer.innerHTML = Object.values(item.TourQuestionOptions)
          .map(option =>
            questionOption({
              text: option.Text.replace('{builderName}', brandName),
              questionId: item.Id,
              questionOptionId: option.Id,
              parentOptionId: option.ParentOptionId,
              parentText: item.Text
            })
          )
          .join('');
      }
    });
  }

  render() {
    return `
      <div class="${this.htmlClass}">
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Reason for touring</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>
        </div>

        <div class="nhs-o-flex-column-items-start nhs-u-gap--700" data-self-guided-tour-questions-list>
        </div>
      </div>
    `;
  }
}
